import React, { useCallback, useMemo } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import HelpPanelButton from '@atlassian/jira-help-panel-button/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { Link } from '@atlassian/react-resource-router';
import messages from './messages.tsx';

type HelpArticle = {
	helpArticleId: string;
	href?: string;
};

type Props = {
	hrefLearnMore?: string;
	message: string;
	analyticsCellName?: string;
	helpArticle: HelpArticle;
	onHandleLearnMore?: () => void;
};

const SelectEditError = ({ helpArticle, message, analyticsCellName, onHandleLearnMore }: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleLearnMore = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'learnMore', {
			cell: analyticsCellName,
		});
		onHandleLearnMore && onHandleLearnMore();
	}, [analyticsCellName, createAnalyticsEvent, onHandleLearnMore]);

	const { href, helpArticleId } = helpArticle;
	const fallbackAnchor = useMemo(
		() =>
			href !== undefined && (
				<Link href={href} onClick={handleLearnMore}>
					{formatMessage(messages.learnMore)}
				</Link>
			),
		[formatMessage, handleLearnMore, href],
	);

	return (
		<Box xcss={wrapperStyles}>
			{message}
			<Box as="span" xcss={helpLinkStyles}>
				<HelpPanelButton
					articleId={helpArticleId}
					type="HELP_ARTICLE"
					appearance="link"
					spacing="none"
					onClick={handleLearnMore}
					fallbackComponent={fallbackAnchor}
				>
					{formatMessage(messages.learnMore)}
				</HelpPanelButton>
			</Box>
		</Box>
	);
};

export default SelectEditError;

const wrapperStyles = xcss({
	height: token('space.600'),
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
});

const helpLinkStyles = xcss({
	marginLeft: 'space.100',
});
